import {
  isTaskRateLimited,
  startTrackedTask,
} from '@/modules/shared/utils/startTrackedTask'
import { taskScopeKey } from '@/modules/shared/utils/taskScopeKey'
import React from 'react'

import Tooltip from '../ToolTip'
import {
  faCalendarDaysSolid,
  faCalendarRangeSolid,
  faCalendarWeekSolid,
  faClockDeskSolid,
  faClose,
  FaIcon,
  faPrintSolid,
  faReceiptSolid,
  faRefreshSolid,
  faTrash,
  faTruckSolid,
} from '@/lib/fa'
import { format } from 'date-fns'
import { DateRangeFilter } from '@/app/constants/delegation/dateRangeFilter'

import { toast } from '@/lib/toasts/api'
import { useDate } from '../../stores/dateStore'
import { TripGenerationMode } from '@/app/constants/delegation/tripGenerationMode'
import useBusinessTripService from '../../services/BusinessTripService'
import usePrintService from '../../services/PrintService'
import { useTask } from '@/modules/shared/context/TaskContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { BusinessTripsPrintModal } from '../../tabs/businessTrips/components'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'
import { createPortal } from 'react-dom'
import { ReportType } from '@/app/constants/reportType'
import { TaskType } from '@/app/constants/taskType'
import { TRIPS_FILTER_OPTIONS } from '@/modules/shared/constants/trips'
import { getDateRangeFilterName } from '@/app/common/converters/newDelegation/dateRangeFilterToTextConverter'
import { useSideSelectionStore } from '../../stores/sideSelectionStore'
import { useBusinessTripFailuresStore } from '../../stores/businessTripFailuresStore'
import { usePrintJobGuard } from '@/modules/printouts/hooks/usePrintJobGuard'
import { useBatchPrintoutStore } from '@/modules/printouts/stores/batchPrintoutStore'
import { useInvalidateDriverRequiredActions } from '@/lib/api/company/drivers/useQueryDriverRequiredActions'
import {
  CurrencyTableIssue,
  findCurrencyTableIssueDrivers,
  getCurrencyTableIssueDetails,
  getCurrencyTableIssueTitle,
} from '@/modules/shared/utils/currencyTableIssue'

const BusinessTripActionBar = ({
  companyId,
  onRefresh,
  selectedTrips,
  dateRangeFilter,
  onDateRangeFilterChange,
  setShowAuditDates,
  showAuditDates,
  tripCount = 0,
  onDeleteAll,
}) => {
  const { t } = useLocalize()
  const { sideCheckedIds } = useSideSelectionStore()
  const { formatApiDate, range } = useDate()
  const { generateTripsBatchTask, checkTripActiveContracts, checkTripCoDrivers } =
    useBusinessTripService()

  // Pre-flight missing-contracts dialog (same UX as the timesheet screen's
  // "Przelicz dla pozostałych"): generation params captured while the dialog
  // decides between cancel and generate-for-the-others.
  // Co-driver question, asked BEFORE generating: the batch then saves crew drivers
  // itself instead of handing them back to the browser unsaved (which used to cost one
  // save request per driver afterwards).
  const [coDriverQuestion, setCoDriverQuestion] = React.useState<{
    list: { driverId: number; driverName: string }[]
    generationMode: TripGenerationMode
    driverIds: Array<string | number>
  } | null>(null)

  const [missingContracts, setMissingContracts] = React.useState<{
    list: { driverId: number; driverName: string }[]
    generationMode: TripGenerationMode
    allMissing: boolean
  } | null>(null)
  const { getPdf } = usePrintService()

  const { addTask, tasks, completedTasks } = useTask()
  const [generationTaskIds, setGenerationTaskIds] = React.useState<string[]>([])
  const [currencyRetry, setCurrencyRetry] = React.useState<{
    issue: CurrencyTableIssue
    driverIds: number[]
    generationMode: number
  } | null>(null)
  const [printModalOpen, setPrintModalOpen] = React.useState(false)
  const { isGenerating, busyTooltip, runPrintJob } = usePrintJobGuard()
  
  const isAnySelected = sideCheckedIds.length > 0
  const isBusinessTripGenerationInProgress = tasks.some(
    (task) => task.type === TaskType.BusinessTrip,
  )
  const dateRange = React.useMemo(() => {
    if (!range?.startDate || !range?.endDate) return undefined
    return {
      startDate: new Date(
        range.startDate.year!,
        range.startDate.month! - 1,
        range.startDate.day!,
      ),
      endDate: new Date(
        range.endDate.year!,
        range.endDate.month! - 1,
        range.endDate.day!,
      ),
    }
  }, [range])

  React.useEffect(() => {
    const completedGenerationTasks = completedTasks.filter((task) =>
      generationTaskIds.includes(task.id),
    )

    if (completedGenerationTasks.length === 0) {
      return
    }

    onRefresh?.()
    setGenerationTaskIds((taskIds) =>
      taskIds.filter(
        (taskId) => !completedGenerationTasks.some((task) => task.id === taskId),
      ),
    )

    // Data kursu jest wspólna dla okresu, więc brak kursu kładzie zwykle całą paczkę.
    // Zamiast zostawiać użytkownika z samym komunikatem, proponujemy powtórzenie
    // generowania z kursem zapasowym — jednym pytaniem dla wszystkich kierowców,
    // tak jak WPF ustawia ForcedExchangeRateDate raz na całą sesję.
    for (const task of completedGenerationTasks) {
      const generationMode = (task.data as { generationMode?: number })
        ?.generationMode
      if (typeof generationMode !== 'number') continue

      let results: unknown
      try {
        results = JSON.parse(task.logs ?? '')
      } catch {
        continue
      }

      const candidates = findCurrencyTableIssueDrivers(results as never)
      if (candidates) {
        setCurrencyRetry({ ...candidates, generationMode })
        break
      }
    }
  }, [completedTasks, generationTaskIds, onRefresh])

  // Masowe "Odśwież koszty": wszystkie delegacje wczytane dla zaznaczonych kierowców
  // i bieżącego okresu, JEDNYM żądaniem. Pracę wykonuje fileprocessor
  // (RefreshTripCostsJob), więc 250 delegacji to jedno zadanie zamiast 250 wywołań,
  // a robota przeżywa zamknięcie karty i restart api.
  const { refreshTripCostsBatchTask } = useBusinessTripService()
  const invalidateDriverRequiredActions = useInvalidateDriverRequiredActions()
  const [costsRefreshConfirm, setCostsRefreshConfirm] = React.useState(false)
  const [costsRefreshTaskIds, setCostsRefreshTaskIds] = React.useState<string[]>([])
  const allTrips = selectedTrips || []
  const isCostsRefreshRunning = costsRefreshTaskIds.length > 0

  React.useEffect(() => {
    const finished = costsRefreshTaskIds.filter((taskId) =>
      completedTasks.some((task) => task.id === taskId),
    )
    if (finished.length === 0) {
      return
    }

    // Odświeżenie zmienia delegacje i unieważnia ewidencję, więc wymagane akcje
    // kierowcy (ostrzeżenia przy nazwiskach) trzeba przeładować.
    invalidateDriverRequiredActions()
    onRefresh?.()
    setCostsRefreshTaskIds((taskIds) =>
      taskIds.filter((taskId) => !finished.includes(taskId)),
    )
  }, [completedTasks, costsRefreshTaskIds, invalidateDriverRequiredActions, onRefresh])

  const runCostsRefresh = async () => {
    const trips = allTrips
      .map((trip: any) => ({
        tripId: Number(trip.id),
        driverId: Number(trip.driver?.id ?? trip.driver),
      }))
      .filter(
        (trip) => Number.isFinite(trip.tripId) && Number.isFinite(trip.driverId),
      )
    if (trips.length === 0) {
      return
    }

    try {
      // Refreshing costs is scoped to the whole company on the server, so a second
      // run while one is in flight is refused with the id of the running task. We
      // follow that one instead of reporting an error the user cannot act on.
      const { taskId, adopted } = await startTrackedTask({
        companyId,
        type: TaskType.BusinessTrip,
        scopeKey: taskScopeKey.businessTripCostsRefresh(companyId),
        data: {
          // Znacznik dla paska postepu: pod tym samym TaskType ida takze generowania
          // delegacji, a komunikat ma mowic, co sie naprawde dzieje.
          operation: 'refreshCosts',
          progressCurrent: 0,
          progressTotal: trips.length,
        },
        progressTotal: trips.length,
        start: () => refreshTripCostsBatchTask({ companyId, trips }),
      })

      if (taskId) {
        setCostsRefreshTaskIds((taskIds) => [...taskIds, taskId])
      }
      if (adopted) {
        // Odswiezanie kosztow ma zakres firmowy, wiec biegnace zadanie moze obejmowac
        // inny zestaw delegacji niz wlasnie zaznaczony - komunikat musi to powiedziec,
        // zamiast sugerowac, ze wszystko sie liczy.
        toast.info({
          title: t('businessTrip.costs-refresh-in-progress'),
          text: t('businessTrip.costs-refresh-already-running'),
        })
      }
    } catch (error) {
      if (isTaskRateLimited(error)) {
        toast.warning({
          text: t('timesheet.too-many-tasks'),
          title: t('businessTrip.costs-refresh-in-progress'),
        })
        return
      }
      console.error('Error refreshing trip costs:', error)
      toast.error({
        text: 'Nie udało się uruchomić odświeżania kosztów',
        title: t('error.unknown'),
      })
    }
  }

  const handleGenerateTrips = async (generationMode: TripGenerationMode) => {
    if (isBusinessTripGenerationInProgress) {
      return
    }

    if (!companyId || !dateRange) {
      toast.warning({
        text: t('menu.select-date-range'),
      })
      return
    }

    if (!sideCheckedIds || sideCheckedIds.length === 0) {
      toast.warning({
        text: t('menu.select-drivers'),
      })
      return
    }

    // Pre-flight: same dialog the timesheet calculation shows — warn about
    // drivers with no active contract BEFORE minutes of generation start.
    try {
      const missing = await checkTripActiveContracts({
        companyId,
        driverIds: sideCheckedIds,
        defaultStartDate: dateRange.startDate,
        defaultEndDate: dateRange.endDate,
      })
      if (missing.length > 0) {
        setMissingContracts({
          list: missing,
          generationMode,
          allMissing: missing.length >= sideCheckedIds.length,
        })
        return
      }
    } catch (error) {
      // Best-effort: a failed pre-check must not block generation — the batch
      // itself reports the same drivers as skipped.
      console.error('Active-contract pre-check failed:', error)
    }

    // Ask about copying to co-drivers BEFORE generating. Answered up front, the batch
    // saves crew drivers in the same partitioned pass as everyone else; answered after
    // the fact, every one of them came back unsaved and cost its own save request.
    try {
      const crewDrivers = await checkTripCoDrivers({
        companyId,
        driverIds: sideCheckedIds,
        defaultStartDate: dateRange.startDate,
        defaultEndDate: dateRange.endDate,
      })
      if (crewDrivers.length > 0) {
        setCoDriverQuestion({
          list: crewDrivers,
          generationMode,
          driverIds: sideCheckedIds,
        })
        return
      }
    } catch (error) {
      // Best-effort: a failed pre-check must not block generation, it just means the
      // copies are not offered for this run.
      console.error('Co-driver pre-check failed:', error)
    }

    await startGeneration(generationMode, sideCheckedIds)
  }

  const startGeneration = async (
    generationMode: TripGenerationMode,
    driverIds: Array<string | number>,
    copyToCoDriver = false,
    withPreviousExchangeRateDate = false,
  ) => {
    if (!companyId || !dateRange) {
      return
    }

    // Drop failure markers from a previous run before starting a new one.
    useBusinessTripFailuresStore.getState().clearFailures()

    try {
      const result = await generateTripsBatchTask({
        companyId,
        driverIds,
        defaultStartDate: dateRange.startDate,
        defaultEndDate: dateRange.endDate,
        events: [],
        generationMode: generationMode as number,
        copyToCoDriver,
        withPreviousExchangeRateDate,
      })

      if (result?.taskId) {
        addTask(result.taskId, TaskType.BusinessTrip, {
          generationMode: generationMode as number,
          defaultStartDate: dateRange.startDate,
          defaultEndDate: dateRange.endDate,
          progressCurrent: 0,
          progressTotal: driverIds.length,
        })
        setGenerationTaskIds((taskIds) => [...taskIds, result.taskId])
      }
    } catch (error) {
      console.error('Error generating trips for drivers:', error)
      toast.error({
        text: t('businessTrip.generate-failed'),
        title: t('error.generate'),
      })
      return
    }
  }

  const handlePrintGenerate = React.useCallback(
    (
      selectedReports: ReportType[],
      options: { generateToSeparateFiles: boolean; rememberSelected: boolean },
    ) =>
    runPrintJob(async () => {
      if (!range?.startDate || !range?.endDate || !formatApiDate) {
        toast.warning({
          text: t('menu.select-date-range') || 'Wybierz zakres dat',
        })
        return
      }

      const printDriverIds = sideCheckedIds

      if (printDriverIds.length === 0) {
        toast.warning({
          text: t('menu.select-drivers'),
        })
        return
      }

      if (selectedReports.length === 0) {
        return
      }

      try {
        const startDate = new Date(
          range.startDate.year!,
          range.startDate.month! - 1,
          range.startDate.day!,
        )
        const endDate = new Date(
          range.endDate.year!,
          range.endDate.month! - 1,
          range.endDate.day!,
        )

        const startTime = formatApiDate(startDate)
        const endTime = encodeURIComponent(
          `${format(endDate, 'YYYY-MM-DD')} 23:59`,
        )

        const tripsByDriver = new Map<string, number[]>()
        const driverNames = new Map<string, string>()
        for (const trip of selectedTrips ?? []) {
          const driver = trip.driver
          const driverId = String(driver?.id || driver || '')
          if (!driverId) continue
          if (!tripsByDriver.has(driverId)) tripsByDriver.set(driverId, [])
          tripsByDriver.get(driverId)!.push(Number(trip.id))
          if (!driverNames.has(driverId)) {
            const label = (
              driver?.fullName || `${driver?.surname ?? ''} ${driver?.name ?? ''}`
            ).trim()
            if (label) driverNames.set(driverId, label)
          }
        }

        if (tripsByDriver.size === 0) {
          toast.warning({
            text: t('businessTrip.no-data'),
          })
          return
        }

      
        const { startBatch, trackTask, sealBatch } =
          useBatchPrintoutStore.getState()
        startBatch(Array.from(driverNames.values()))

        for (const reportType of selectedReports) {
          for (const [driverId, tripIds] of tripsByDriver) {
            try {
              const taskId = await getPdf({
                companyId,
                reportType,
                drivers: [driverId],
                startTime,
                endTime,
                data: {
                  trips: tripIds,
                  generateToSeparateFiles: options.generateToSeparateFiles,
                  rememberSelected: options.rememberSelected,
                  onlyPolish: true,
                  tripReportOption: dateRangeFilter,
                },
              })
              trackTask(taskId, reportType)
              addTask(taskId, TaskType.Printout, reportType)
            } catch (error) {
              console.error(
                `Error generating print for ${reportType} driver ${driverId}:`,
                error,
              )
            }
          }
        }

        sealBatch()

        setPrintModalOpen(false)
      } catch (error) {
        console.error('Error generating print:', error)
        toast.error({
          text: t('error.print-failed'),
        })
      }
    }),
    [
      range,
      formatApiDate,
      companyId,
      sideCheckedIds,
      selectedTrips,
      dateRangeFilter,
      getPdf,
      addTask,
      runPrintJob,
      t,
    ],
  )

  return (
    <div className="tw-relative tw-z-[100] tw-w-full tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-py-2">
      <div className="tw-flex tw-w-full tw-justify-between tw-px-4 tw-py-1.5 md:tw-items-center">
        <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-center">
          <div className="tw-flex tw-items-stretch tw-rounded-md tw-bg-[#6B77A0]/30">
            {TRIPS_FILTER_OPTIONS.filter(
              (o) =>
                o.value !== DateRangeFilter.Calculated &&
                o.value !== DateRangeFilter.SettlementMonth &&
                o.value !== DateRangeFilter.AllButSettlementMonth &&
                (showAuditDates || o.value !== DateRangeFilter.Modified),
            ).map((option, index, arr) => {
              const filterName = getDateRangeFilterName(option.value, t)
              const isActive = dateRangeFilter === option.value
              const isFirst = index === 0
              const isLast = index === arr.length - 1
              return (
                <Tooltip portal zIndex={300} key={option.value} text={filterName} disabled={!isAnySelected}>
                  <div
                    onClick={() => onDateRangeFilterChange(option.value)}
                    className={`tw-grid tw-cursor-pointer tw-place-items-center tw-p-1 ${
                      isFirst ? 'tw-rounded-s-md' : ''
                    } ${isLast ? 'tw-rounded-e-md' : ''} ${
                      isActive ? 'tw-bg-primary' : 'tw-bg-none'
                    }`}
                  >
                    <img
                      src={option.icon}
                      alt={filterName}
                      className="tw-h-4 tw-w-4 tw-brightness-0 tw-invert"
                    />
                  </div>
                </Tooltip>
              )
            })}
          </div>
          <Tooltip portal zIndex={300} text={'Pokaż daty modyfikacji i utworzenia'} disabled={!isAnySelected}>
            <div
              className={`tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded ${
                showAuditDates ? 'tw-bg-primary' : 'tw-bg-primary/30'
              } tw-cursor-pointer`}
              onClick={() => setShowAuditDates(!showAuditDates)}
            >
              <FaIcon
                icon={faClockDeskSolid}
                className="tw-h-4 tw-w-4 tw-text-white"
              />
            </div>
          </Tooltip>
          <div className="tw-flex tw-items-center tw-gap-2">
            {/* Daily */}
            <Tooltip portal zIndex={300}
              text={t('groupedRibbon.dailyTitle')}
              onClick={() => handleGenerateTrips(TripGenerationMode.Daily)}
          disabled={!isAnySelected || isBusinessTripGenerationInProgress}>
              <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faCalendarDaysSolid}
                  className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
                />
              </div>
            </Tooltip>

            {/* Weekly */}
            <Tooltip portal zIndex={300}
              text={t('groupedRibbon.weeklyTitle')}
              onClick={() => handleGenerateTrips(TripGenerationMode.Weekly)}
          disabled={!isAnySelected || isBusinessTripGenerationInProgress}>
              <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faCalendarWeekSolid}
                  className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
                />
              </div>
            </Tooltip>

            {/* Monthly Manual Entry */}
            <Tooltip portal zIndex={300}
              text={t('groupedRibbon.monthManualEntryTitle')}
              onClick={() =>
                handleGenerateTrips(TripGenerationMode.MonthManualEntry)
              }
           disabled={!isAnySelected || isBusinessTripGenerationInProgress}>
              <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faCalendarRangeSolid}
                  className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
                />
              </div>
            </Tooltip>

            {/* Monthly Manual Entry 9h */}
            <Tooltip portal zIndex={300}
              text={t('groupedRibbon.monthManualEntry9Title')}
              onClick={() =>
                handleGenerateTrips(TripGenerationMode.MonthManualEntry9)
              }
           disabled={!isAnySelected || isBusinessTripGenerationInProgress}>
              <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                <CalendarIconWithBadge badge="9" />
              </div>
            </Tooltip>

            {/* Monthly Manual Entry 24h */}
            <Tooltip portal zIndex={300}
              text={t('groupedRibbon.monthManualEntry24Title')}
              onClick={() =>
                handleGenerateTrips(TripGenerationMode.MonthManualEntry24)
              }
          disabled={!isAnySelected || isBusinessTripGenerationInProgress}>
              <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                <CalendarIconWithBadge badge="24" />
              </div>
            </Tooltip>

            {/* Monthly Manual Entry 45h */}
            <Tooltip portal zIndex={300}
              text={t('groupedRibbon.monthManualEntry45Title')}
              onClick={() =>
                handleGenerateTrips(TripGenerationMode.MonthManualEntry45)
              }
           disabled={!isAnySelected || isBusinessTripGenerationInProgress}>
              <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                <CalendarIconWithBadge badge="45" />
              </div>
            </Tooltip>

            <Tooltip portal zIndex={300}
              text={t('groupedRibbon.foreignTitle')}
              onClick={() => handleGenerateTrips(TripGenerationMode.Foreign)}
          disabled={!isAnySelected || isBusinessTripGenerationInProgress}>
              <div className="tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-[#e5e6e6]">
                <FaIcon
                  icon={faTruckSolid}
                  className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
                />
              </div>
            </Tooltip>
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-space-x-4">
          <Tooltip portal zIndex={300}
            text={
              tripCount > 0
                ? 'Usuń wszystkie delegacje (dla kierowców z obszaru roboczego)'
                : 'Brak delegacji do usunięcia'
            }
            onClick={() => {
              if (tripCount > 0) onDeleteAll?.()
            }}
          >
            <FaIcon
              icon={faTrash}
              className={`tw-h-5 tw-w-5 tw-text-red-600 ${
                tripCount > 0
                  ? 'tw-cursor-pointer hover:tw-opacity-70'
                  : 'tw-cursor-not-allowed tw-opacity-40'
              }`}
            />
          </Tooltip>
          <Tooltip portal zIndex={300}
            text={
              isCostsRefreshRunning
                ? 'Odświeżanie kosztów w toku…'
                : allTrips.length > 0
                  ? `Odśwież koszty we wszystkich delegacjach okresu (${allTrips.length})`
                  : 'Brak delegacji do odświeżenia'
            }
            onClick={() => {
              if (
                allTrips.length > 0 &&
                !isCostsRefreshRunning &&
                !isBusinessTripGenerationInProgress
              ) {
                setCostsRefreshConfirm(true)
              }
            }}
          >
            <FaIcon
              icon={faReceiptSolid}
              className={`tw-h-5 tw-w-5 tw-text-primary ${
                allTrips.length > 0 &&
                !isCostsRefreshRunning &&
                !isBusinessTripGenerationInProgress
                  ? 'tw-cursor-pointer hover:tw-opacity-70'
                  : 'tw-cursor-not-allowed tw-opacity-40'
              }`}
            />
          </Tooltip>
          <Tooltip portal zIndex={300} text={t('menu.refresh')} onClick={() => onRefresh?.()}>
            <FaIcon
              icon={faRefreshSolid}
              className="tw-h-5 tw-w-5 tw-text-primary hover:tw-opacity-70"
            />
          </Tooltip>
          <Tooltip portal zIndex={300}
            text={t('menu.print')}
            onClick={() => setPrintModalOpen(true)}
            blockedText={busyTooltip}
          >
            <FaIcon
              icon={faPrintSolid}
              className="tw-h-5 tw-w-5 tw-text-primary hover:tw-opacity-70"
            />
          </Tooltip>
        </div>
      </div>

      {createPortal(
        <BusinessTripsPrintModal
          isOpen={printModalOpen}
          onClose={() => setPrintModalOpen(false)}
          companyId={companyId}
          driverIds={sideCheckedIds}
          startTime={dateRange?.startDate ?? new Date()}
          endTime={
            dateRange?.endDate
              ? new Date(
                  dateRange.endDate.getFullYear(),
                  dateRange.endDate.getMonth(),
                  dateRange.endDate.getDate(),
                  23,
                  59,
                  0,
                  0,
                )
              : new Date()
          }
          onGenerate={handlePrintGenerate}
          pending={isGenerating}
        />,
        document.body,
      )}

      {/* Co-driver copy question, asked before generating so the batch can save crew
          drivers itself. Three answers: copy, do not copy, back out. */}
      <ConfirmModal
        isOpen={costsRefreshConfirm}
        onClose={() => setCostsRefreshConfirm(false)}
        title="Odśwież koszty delegacji"
        text={`Wygenerować od nowa koszty w ${allTrips.length} delegacjach z bieżącego okresu? Pozycje „Sanitariaty" i „Zwrot kosztów noclegu" zostaną utworzone na nowo, pozycje dodane ręcznie zostaną zachowane. Diety, noclegi i zdarzenia pozostaną bez zmian.`}
        cancelText={t('common.cancel')}
        actionText={t('menu.refresh')}
        onConfirm={async () => {
          setCostsRefreshConfirm(false)
          await runCostsRefresh()
        }}
      />

      <ConfirmModal
        isOpen={coDriverQuestion !== null}
        onClose={() => setCoDriverQuestion(null)}
        title={t('businessTrip.copy-to-codrivers-title')}
        text={t('businessTrip.copy-to-codrivers-question')}
        content={coDriverQuestion?.list.map((entry) => entry.driverName) ?? []}
        cancelText={t('common.cancel')}
        secondaryText={t('answer.no')}
        onSecondary={async () => {
          const question = coDriverQuestion
          setCoDriverQuestion(null)
          if (question) {
            await startGeneration(question.generationMode, question.driverIds, false)
          }
        }}
        actionText={t('answer.yes')}
        onConfirm={async () => {
          const question = coDriverQuestion
          setCoDriverQuestion(null)
          if (question) {
            await startGeneration(question.generationMode, question.driverIds, true)
          }
        }}
      />

      {/* Brak kursu NBP położył zapis całej paczki — jedno pytanie zamiast wracania
          do każdego kierowcy z osobna. */}
      <ConfirmModal
        isOpen={currencyRetry !== null}
        onClose={() => setCurrencyRetry(null)}
        title={
          currencyRetry ? getCurrencyTableIssueTitle(currencyRetry.issue, t) : ''
        }
        text={
          currencyRetry
            ? `${getCurrencyTableIssueDetails(currencyRetry.issue, t)}\n\n${t(
                'businessTrip.currency-retry-question',
              ).replace('{0}', String(currencyRetry.driverIds.length))}`
            : ''
        }
        cancelText={t('common.cancel')}
        actionText={t('newDelegation.use-last-available-rate')}
        onConfirm={async () => {
          const retry = currencyRetry
          setCurrencyRetry(null)
          if (retry) {
            await startGeneration(
              retry.generationMode as TripGenerationMode,
              retry.driverIds,
              false,
              true, // withPreviousExchangeRateDate
            )
          }
        }}
      />

      {/* Missing-contract warning — mirrors the timesheet calculation dialog. */}
      <ConfirmModal
        isOpen={missingContracts !== null}
        onClose={() => setMissingContracts(null)}
        title={t('timesheet.insufficient-data-title')}
        text={t('timesheet.contract-not-found-title')}
        content={missingContracts?.list.map((entry) => entry.driverName) ?? []}
        actionText={t('businessTrip.generate-for-others')}
        onConfirm={
          missingContracts && !missingContracts.allMissing
            ? async () => {
                const excluded = new Set(
                  missingContracts.list.map((entry) => entry.driverId),
                )
                const remaining = sideCheckedIds.filter(
                  (id) => !excluded.has(Number(id)),
                )
                const mode = missingContracts.generationMode
                setMissingContracts(null)
                await startGeneration(mode, remaining)
              }
            : undefined
        }
      />
    </div>
  )
}

export default BusinessTripActionBar

const CalendarIconWithBadge = ({ badge }: { badge: string }) => (
  <div className="tw-relative tw-inline-flex">
    <FaIcon
      icon={faCalendarDaysSolid}
      className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
    />
    <div className="tw-absolute -tw-right-1 -tw-top-1 tw-flex tw-h-3 tw-w-3 tw-items-center tw-justify-center tw-rounded-full tw-bg-[#2448bb]">
      <span className="tw-text-[8px] tw-font-medium tw-leading-none tw-text-white">
        {badge}
      </span>
    </div>
  </div>
)
